import type { ReactNode } from 'react'
import { TextField, Select } from '@/components/ds2'
import { formatUsd } from '../../results/format'
import { ChargeAmountField, ChargeDescriptionField } from './ChargesEditor'
import { ReviewNotice, SectionLabel } from './ReviewNotice'
import { PAYMENT_TERMS_OPTIONS, type ChargeDraft } from './reviewModel'

export interface InvoiceDetailsValues {
    invoiceNumber: string
    invoiceDate: string
    terms: string
    charges: ChargeDraft[]
    /** The linehaul as typed, when the load has no rate yet; otherwise unused. */
    rateText: string
}

interface InvoiceDetailsSectionProps {
    values: InvoiceDetailsValues
    onChange: (patch: Partial<InvoiceDetailsValues>) => void
    /** The load's linehaul when it has a rate (dollars), or null when the rate is still to be set. */
    linehaul: number | null
    /** The first problem with the typed rate or charges, shown under the form. */
    error: string | null
    /** The invoice total: the linehaul plus the charges above zero. */
    total: number | null
    /** What the drawer inserts above the error line (v17: Skip & submit's notice and reason). */
    beforeError?: ReactNode
}

const dateInput = 'w-full rounded-xl bg-surface px-3 py-2 text-sm text-foreground shadow-field outline-none focus:ring-2 focus:ring-accent'

/**
 * The invoice details of the review drawer (v17 openLoad): a two-column grid of number (empty = the next Numeo number), date, payment
 * terms and the approved additional charges with their type; then the linehaul (editable when the load has no rate), the total and the
 * note that creating a draft sends nothing. Its own values are kept by the drawer.
 */
export const InvoiceDetailsSection = ({ values, onChange, linehaul, error, total, beforeError }: InvoiceDetailsSectionProps) => {
    const charges = { charges: values.charges, onChange: (next: ChargeDraft[]) => onChange({ charges: next }) }
    return (
        <section aria-label="Invoice details">
            <SectionLabel>Invoice details</SectionLabel>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <TextField
                    label="Invoice number"
                    description="Next number assigned when the draft is created"
                    value={values.invoiceNumber}
                    onChange={(event) => onChange({ invoiceNumber: event.target.value })}
                />
                <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
                    Invoice date
                    <input type="date" className={dateInput} value={values.invoiceDate} onChange={(event) => onChange({ invoiceDate: event.target.value })} />
                </label>
                <Select
                    label="Payment terms"
                    options={PAYMENT_TERMS_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
                    value={values.terms}
                    onChange={(terms) => onChange({ terms })}
                />
                <ChargeAmountField {...charges} />
            </div>
            <div className="mt-4 flex flex-col gap-4">
                <ChargeDescriptionField {...charges} />
                {linehaul === null ? (
                    <TextField
                        label="Linehaul rate ($)"
                        inputMode="decimal"
                        placeholder="0.00"
                        description="The load has no rate yet. It is saved to the load before the invoice is created."
                        value={values.rateText}
                        onChange={(event) => onChange({ rateText: event.target.value })}
                    />
                ) : (
                    <p className="flex items-center justify-between text-xs text-foreground-muted">
                        <span>Linehaul rate</span>
                        <strong className="font-semibold text-foreground">{formatUsd(linehaul)}</strong>
                    </p>
                )}
                <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold text-foreground">Invoice total</h3>
                    <h2 className="text-xl font-semibold text-foreground" aria-label="Invoice total">
                        {formatUsd(total)}
                    </h2>
                </div>
                {beforeError}
                {error && (
                    <p role="alert" className="text-xs text-danger">
                        {error}
                    </p>
                )}
            </div>
            <ReviewNotice tone="info">
                Creating a draft adds it to Accounting → Invoices (AR). Sending to the broker or factoring provider is a separate action.
            </ReviewNotice>
        </section>
    )
}
